






































































































































































/**
 * NOTE: When using the sync modifier, objects must always be passed with v-bind!
 *  ALl custom validations should evaluate to true to submit form. Invalid = false
 */
import { Component, Mixins, Prop } from "vue-property-decorator";
import {
  ChairOrderRecord,
  Order,
  OrderDetails,
  UmbrellaOrderRecord
} from "@/models/order/order";
import OrderService from "@/services/order_service";
import { GenericError } from "@/services/error_service";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderActions, OrderGetters } from "@/store/modules/order/types";
import {
  CustomChairOrder,
  CustomUmbrellaOrder
} from "@/models/configurator/custom_umbrella";
import { ProductType } from "@/models/products/product";
import { Mobile } from "@/mixins/mobile";
import OrderSummary from "@/components/checkout/OrderSummary.vue";
import ReviewFooter from "@/components/footer/ReviewFooter.vue";
import ConfirmRequestSalesOrder from "@/components/elements/modal/ConfirmRequestSalesOrder.vue";
import { NotificationService } from "@/services/notification_service";
import {
  CreateNetSuiteCustomerPayload,
  CreateNetSuiteQuotePayload,
  PaymentTerm
} from "@/models/netsuite/netsuite";
import { OrderMixin } from "@/mixins/orders/mixin";
import { GlobalGetters } from "@/store/modules/global/types";
import { SSOImpersonation } from "@/services/auth_service";
import { PriceLevel } from "@/models/netsuite/netsuite";
import {
  NetSuiteEntity,
  NetSuiteEntityStatus
} from "@/models/netsuite/helpers";

@Component({
  components: {
    OrderSummary,
    ReviewFooter,
    ConfirmRequestSalesOrder
  }
})
export default class OrderReview extends Mixins(Mobile, OrderMixin) {
  @(namespace(StoreModule.Order).Action(OrderActions.AddShipping))
  addShipping!: (value: OrderDetails["shipping_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddBilling))
  addBilling!: (value: OrderDetails["billing_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddPayment))
  addPayment!: (value: OrderDetails["payment_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddContact))
  addContact!: (value: OrderDetails["contact_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AddOrderId))
  addOrderId!: (value: { id: string }) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AssignCustomer))
  assignCustomer!: (value: {
    customer_id: string;
    customer_name: string;
    customer_netsuite_id: number | undefined;
    customer_netsuite_contact_id: number | undefined;
    customer_netsuite_payment_terms_id: number | undefined;
    customer_netsuite_price_level_id: number | undefined;
    customer_netsuite_price_level_discount: number | undefined;
    order_id: string | null;
  }) => Promise<void>;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetMultiplier))
  multiplier!: PriceLevel | null;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetImpersonation))
  impersonation!: SSOImpersonation | null;
  protected loader: any = null;
  protected newlyCreatedCustomerId: number | null = null;
  protected orderService = new OrderService();
  protected error = {
    billing: false,
    shipping: false,
    payment: false,
    contact: false
  };
  @Prop() props!: any;

  created() {
    if (!this.props || !this.props.order) {
      this.$router.push("/resubmit?source=review");
    }
    window.scroll(0, 0);
  }

  protected get getPaymentTermText(): string {
    const methodId = this.props.order.payment_details.method;
    if (!methodId && methodId !== 0) return "N/A";
    const paymentTerms: PaymentTerm[] = this.props.paymentTerms || [];
    const term = paymentTerms.find(t => t.id === methodId);
    return term ? term.name : "N/A";
  }

  protected showRequestSOModal = false;

  protected openRequestSalesOrderModal(): void {
    this.showRequestSOModal = true;
  }

  protected closeRequestSalesOrderModal(): void {
    this.showRequestSOModal = false;
  }

  /**
   * Confirmed "Request Sales Order": finalize the quote in NetSuite and ask the
   * order desk to convert it. Runs the exact same save-then-submit flow as a plain
   * submit, but with `requestSalesOrder` set — the draft lands on
   * `pending_sales_order` and becomes read-only.
   */
  protected async confirmRequestSalesOrder(): Promise<void> {
    this.showRequestSOModal = false;
    await this.runCheckout(true);
  }

  protected checkout(): Promise<void> {
    return this.runCheckout(false);
  }

  private async runCheckout(requestSalesOrder: boolean): Promise<void> {
    this.loader = this.$loading.show(
      { opacity: 1 },
      {
        before: [
          this.$createElement(
            "h1",
            requestSalesOrder
              ? "Requesting sales order... please do not refresh the page."
              : "Submitting your quote... please do not refresh the page."
          ),
          this.$createElement(
            "p",
            { class: "vld-subtext" },
            "This may take a couple of minutes. Thank you for your patience!"
          )
        ]
      }
    );
    try {
      await this.saveDataToStore();
      const order = await this.parseItems();
      await this.createQuote(order, requestSalesOrder);
    } catch (err) {
      console.error("[OrderReview] checkout() error:", err);
      GenericError.popup((err as any)?.message ?? "Something went wrong.");
    } finally {
      this.loader.hide();
    }
  }

  protected async saveDataToStore(): Promise<void> {
    await this.addShipping(this.props.order.shipping_details);
    await this.addBilling(this.props.order.billing_details);
    await this.addPayment(this.props.order.payment_details);
    await this.addContact(this.props.order.contact_details);
  }

  /**
   * The API expects only mfr_codes for fabric swatches. We save the entire fabric swatch object on the frontend because the fabric selector needs the extra data to do filtering
   */
  protected async parseItems(): Promise<Order> {
    const items = JSON.parse(JSON.stringify(this.storeOrder.items!));
    for (const item of items) {
      // Lines added directly in NetSuite carry no configuration, or a
      // best-effort SKU-parsed one whose fabrics are already bare mfr codes.
      // There is nothing to parse, and dereferencing it would throw and block
      // the submit — skip them so the quote can still go through.
      if (item.ns_locked || !item.item_details) {
        continue;
      }
      if (item.product_type === ProductType.Umbrella) {
        const parsedData = this.parseFabricFields(
          (item.item_details as CustomUmbrellaOrder)
            .canopy as CustomUmbrellaOrder["canopy"]
        );
        (item.item_details as CustomUmbrellaOrder).canopy = Object.assign(
          {},
          { ...(item.item_details as CustomUmbrellaOrder).canopy },
          { ...parsedData }
        );
      } else if (item.product_type === ProductType.Chair) {
        const parsedData = this.parseFabricFields(
          item.item_details as CustomChairOrder
        );
        (item.item_details as any) = Object.assign(
          {},
          { ...item.item_details },
          { ...parsedData } as ChairOrderRecord
        );
      }
    }
    const order = { ...this.storeOrder };
    order.items = [...items];
    return order;
  }

  /**
   * API is expecting strings for fabrics. parseFabricFields() convert swatch objects to strings if value is not already a string
   */
  protected parseFabricFields(
    fabric: CustomUmbrellaOrder["canopy"] | CustomChairOrder
  ): UmbrellaOrderRecord | ChairOrderRecord {
    const fabrics = [
      "mainFabric",
      "mainTrimFabric",
      "mainTrimFabricInner",
      "ventTopFabric",
      "ventMiddleFabric",
      "ventTrimFabric",
      "valanceFabric",
      "bindingFabric",
      "ribFabric",
      "undersideFabric"
    ];
    const parsed = {} as UmbrellaOrderRecord;
    for (const fabricKey of fabrics) {
      if (fabric[fabricKey] && fabric[fabricKey].mfr_code) {
        parsed[fabricKey] = fabric[fabricKey].mfr_code;
      } else if (
        fabric[fabricKey] &&
        Array.isArray(fabric[fabricKey]) &&
        fabric[fabricKey].length > 0
      ) {
        parsed[fabricKey] = fabric[fabricKey].map(swatch => {
          if (swatch.mfr_code) {
            return swatch.mfr_code;
          } else {
            return swatch;
          }
        });
      } else {
        parsed[fabricKey] = fabric[fabricKey];
      }
    }
    return parsed;
  }

  protected async createQuote(
    order: Order,
    requestSalesOrder = false
  ): Promise<void> {
    try {
      // ToDo: FNSI-101 - create NetSuite quote and then create unofficial quote.
      // commenting out the unofficial quote creation for now until we optimize the auth process and can associate quotes with users properly
      // DETERMINING IF WE CAN ELIMINATE THE "UNOFFICIAL" QUOTE CONCEPT ALTOGETHER AND JUST HAVE NETSUITE QUOTES
      this.newlyCreatedCustomerId = null;
      const res = await this.createNetSuiteQuote(order, requestSalesOrder);
      // const quote = await this.orderService.createQuote(order);
      // await this.addOrderId({ id: quote._id!, creator: quote.creator_id });
      const query: Record<string, string> = { type: "quote" };
      if (this.newlyCreatedCustomerId) {
        query.newCustomerId = String(this.newlyCreatedCustomerId);
      }
      if (order.contact_details?.send_email) {
        query.emailSent = "1";
      }
      if (res?.estimate?.estimateId) {
        query.quoteId = String(res.estimate.estimateId);
      }
      if (res?.estimate?.tranId) {
        query.tranId = res.estimate.tranId;
      }
      if (requestSalesOrder) {
        query.salesOrderRequested = "1";
        NotificationService.show(
          "Sales order requested. The order desk has been notified.",
          "success"
        );
      }
      await this.$router.push({ path: "/checkout/submitted", query });
    } catch (err) {
      GenericError.popup(err.message);
      this.$router.push("/cart");
    }
  }

  protected get isNewCustomer(): boolean {
    return !this.storeOrder.customer_netsuite_id;
  }

  protected async createNetSuiteCustomerIfNeeded(order: Order): Promise<Order> {
    if (!this.isNewCustomer) return order;
    const contact = order.contact_details;
    const shipping = order.shipping_details;
    const billing = order.billing_details;
    const email = contact.email;
    const shippingAddress = {
      addressee: shipping?.addressee ?? "",
      attention: shipping?.attention ?? "",
      addr1: shipping?.address?.address_1 ?? "",
      addr2: shipping?.address?.address_2 ?? "",
      city: shipping?.address?.city ?? "",
      state: shipping?.address?.state ?? "",
      zip: shipping?.address?.postal_code ?? "",
      country: shipping?.address?.country ?? "US"
    };
    const hasBilling =
      billing?.address?.address_1 ||
      billing?.address?.city ||
      billing?.address?.postal_code;
    const customerPriceLevelId = this.multiplier?.id ?? 1; // default price level ID of 1 is "Base Price"
    const payload: CreateNetSuiteCustomerPayload = {
      action: "createCustomer",
      recordType: NetSuiteEntity.Prospect,
      entitystatus: NetSuiteEntityStatus.Prospect_Proposal,
      category: "Retail",
      companyname: contact.company_name,
      email,
      phone: shipping?.phone ?? contact.phone,
      invoiceEmail: email,
      salesrep: this.user.netsuite_id,
      pricelevel: customerPriceLevelId,
      ...(order.payment_details?.method
        ? { terms: order.payment_details.method }
        : {}),
      shippingAddress: shippingAddress,
      ...(hasBilling
        ? {
            billingAddress: {
              addressee: billing?.addressee ?? "",
              attention: billing?.attention ?? "",
              addr1: billing?.address?.address_1 ?? "",
              addr2: billing?.address?.address_2 ?? "",
              addr3: billing?.address?.address_3 ?? "",
              city: billing?.address?.city ?? "",
              state: billing?.address?.state ?? "",
              zip: billing?.address?.postal_code ?? "",
              country: billing?.address?.country ?? "US"
            }
          }
        : {}),
      contact: {
        title: contact.title ?? null,
        firstname: contact.first_name,
        lastname: contact.last_name,
        email,
        phone: contact.phone ?? null
      }
    };
    const res = await this.netSuiteService.createNetSuiteCustomer(payload);
    console.log("NetSuite Customer created successfully:", res);
    const newCustomerNetSuiteId: number = res?.customerId;
    const newContactNetSuiteId: number = res?.contactId;
    if (!newCustomerNetSuiteId) {
      throw new Error(
        "Failed to retrieve new customer NetSuite ID from response."
      );
    }
    return {
      ...order,
      customer_netsuite_id: newCustomerNetSuiteId,
      customer_netsuite_price_level_id: customerPriceLevelId,
      customer_netsuite_contact_id: newContactNetSuiteId
    };
  }

  protected async createNetSuiteQuote(
    order: Order,
    requestSalesOrder = false
  ): Promise<any> {
    try {
      const orderWithCustomer = await this.createNetSuiteCustomerIfNeeded(
        order
      );
      if (
        orderWithCustomer.customer_netsuite_id !== order.customer_netsuite_id
      ) {
        this.newlyCreatedCustomerId =
          orderWithCustomer.customer_netsuite_id ?? null;
        // Persist the freshly created NS customer to the store so the draft
        // saved below (and any later edit of it) carries the real ids.
        await this.assignCustomer({
          customer_id: String(orderWithCustomer.customer_netsuite_id),
          customer_name:
            orderWithCustomer.contact_details?.company_name ??
            this.storeOrder.customer_name ??
            "",
          customer_netsuite_id: orderWithCustomer.customer_netsuite_id,
          customer_netsuite_contact_id:
            orderWithCustomer.customer_netsuite_contact_id,
          customer_netsuite_payment_terms_id:
            orderWithCustomer.customer_netsuite_payment_terms_id,
          customer_netsuite_price_level_id:
            orderWithCustomer.customer_netsuite_price_level_id,
          customer_netsuite_price_level_discount:
            orderWithCustomer.customer_netsuite_price_level_discount,
          order_id: null
        });
      }
      // FUPOF-191: every submit goes through the PSQL draft, which owns line
      // identity — the API injects the NetSuite custom columns from PSQL, so
      // the payload carries no custcol_* of its own. The Laravel API forwards
      // it to NS — creating the quote, or updating it when the draft already
      // has a netsuite_quote_id — then flips the draft to submitted.
      const draft = await this.saveDraftAction();
      const payload: CreateNetSuiteQuotePayload = await this.prepOrderToCreateNetSuiteQuote(
        orderWithCustomer
      );
      const res = requestSalesOrder
        ? await this.quoteDraftService.requestSalesOrder(
            draft.id,
            payload,
            // The authenticated user is resolved server-side; only the admin
            // impersonating them (if any) must be passed, since the bearer token
            // can't carry it.
            this.impersonation?.is_impersonating
              ? this.impersonation.impersonator?.name ?? null
              : null
          )
        : await this.quoteDraftService.submitQuote(draft.id, payload);
      if (res?.status !== "success") {
        throw new Error(
          res?.status
            ? `NetSuite returned status: ${res.status}`
            : "NetSuite did not return a success status."
        );
      }
      // The response carries the NS identity (netsuite_quote_id, and ns_line_id
      // per line) — merge it back rather than waiting for a reload to fill it in.
      if (res.quote) {
        await this.applySubmittedDraftAction(res.quote);
      }
      return res;
    } catch (error) {
      console.error("Error creating NetSuite quote:", error);
      throw new Error(
        `Failed to create NetSuite quote. Try again. ${error.message}`
      );
    }
  }

  protected async createOrder(order: Order): Promise<void> {
    try {
      // ToDo: FNSI-101 - create NetSuite quote, request for that quote to be submitted, and then create the unofficial order through the Node API
      const res = await this.orderService.createOrder(order);
      this.$router.push("/checkout/submitted?type=order");
      this.loader.hide();
    } catch (err) {
      this.loader.hide();
      GenericError.popup(err.message);
    }
  }

  protected async convertToOrder(order: Order): Promise<void> {
    try {
      const res = await this.orderService.convertOrderToQuote(
        order._id!,
        order
      );
      this.$router.push("/checkout/submitted?type=order");
      this.loader.hide();
    } catch (err) {
      this.loader.hide();
      GenericError.popup(err.message);
    }
  }
}
